import { useEffect, useState } from 'react';
import { Modal, Pressable, Text, View } from 'react-native';
import { useDoc } from '../store/store';
import { tasksOn } from '../store/planner';
import { today } from '../lib/date';
import { fromMin, toMin } from '../lib/time';
import { C, useCores } from './theme';
import { t } from '../lib/i18n';
import { noAparelho } from '../lib/fuso';
import { horaTela } from '../lib/date';

export type Aviso = { key: string; tipo: 'pausa' | 'volta'; titulo: string; emoji: string; fimSeg: number; cor: string };

/** Aviso grande na tela durante as pausas das tarefas (ex.: blocos de estudo). */
export function PauseOverlay() {
  const { tasks } = useDoc('planner');
  const { corDe } = useCores();
  const [agora, setAgora] = useState(Date.now());
  const [fechados, setFechados] = useState<Record<string, true>>({});
  const [extra, setExtra] = useState<Record<string, number>>({});

  useEffect(() => { const i = setInterval(() => setAgora(Date.now()), 1000); return () => clearInterval(i); }, []);

  const d = new Date(agora);
  const seg = d.getHours() * 3600 + d.getMinutes() * 60 + d.getSeconds();
  const dia = today();
  let aviso: Aviso | null = null;
  for (const tk of tasksOn(dia, tasks)) {
    if (!tk.time || tk.done.includes(dia)) continue;
    const desloc = toMin(noAparelho(tk, dia).hora ?? tk.time) - toMin(tk.time);   // tarefa marcada em outro fuso
    for (const p of tk.pauses) {
      const key = `${tk.id}:${dia}:${p.at}`;
      const ini = (toMin(p.at) + desloc) * 60, fim = ini + (p.min + (extra[key] ?? 0)) * 60;
      const cor = tk.color ?? corDe(tk.area);
      if (seg >= ini && seg < fim && !fechados[key]) aviso = { key, tipo: 'pausa', titulo: tk.title, emoji: tk.emoji, fimSeg: fim, cor };
      else if (seg >= fim && seg < fim + 120 && !fechados[key + ':v'] && (fechados[key] || seg - fim < 120))
        aviso = { key: key + ':v', tipo: 'volta', titulo: tk.title, emoji: tk.emoji, fimSeg: fim, cor };
      if (aviso) break;
    }
    if (aviso) break;
  }
  if (!aviso) return null;
  const a = aviso;
  const fechar = () => setFechados(f => ({ ...f, [a.key]: true }));

  return <AvisoGrande a={a} seg={seg} onMais5={() => setExtra(e => ({ ...e, [a.key]: (e[a.key] ?? 0) + 5 }))} onFechar={fechar} />;
}

/** O aviso grande na tela (0.67: o mesmo visual vale para a sessão de estudo, texto 05 G3).
 *  seg = segundos do dia agora; a.fimSeg = segundos do dia em que a pausa termina. onMais5: sem ele, não mostra "+5 min". */
export function AvisoGrande({ a, seg, onMais5, onFechar, textoPausa, textoVolta, botao }: { a: Aviso; seg: number; onMais5?: () => void; onFechar: () => void; textoPausa?: string; textoVolta?: string;
  botao?: { label: string; onPress: () => void } }) {   // 0.69 (F2.6): botão extra, ex.: "Abrir Matemática" na troca de matéria
  const resta = Math.max(0, a.fimSeg - seg);
  const mm = String(Math.floor(resta / 60)).padStart(2, '0'), ss = String(resta % 60).padStart(2, '0');
  return (
    <Modal visible transparent animationType="fade" onRequestClose={onFechar}>
      <View style={{ flex: 1, backgroundColor: a.tipo === 'pausa' ? 'rgba(14,16,21,0.97)' : a.cor, alignItems: 'center', justifyContent: 'center', padding: 30, gap: 18 }}>
        <Text style={{ fontSize: 80 }}>{a.tipo === 'pausa' ? '☕' : a.emoji}</Text>
        <Text style={{ color: '#fff', fontSize: 34, fontWeight: '900', textAlign: 'center' }}>{a.tipo === 'pausa' ? (textoPausa ?? t('Hora da pausa')) : (textoVolta ?? t('Hora de voltar!'))}</Text>
        <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: 17, textAlign: 'center' }}>{a.titulo}</Text>
        {a.tipo === 'pausa' && <Text style={{ color: a.cor, fontSize: 72, fontWeight: '200', fontVariant: ['tabular-nums'] }}>{mm}:{ss}</Text>}
        {a.tipo === 'pausa' && <Text style={{ color: C.sub }}>{t('Volta às {hora}', { hora: horaTela(fromMin(Math.round(a.fimSeg / 60))) })}</Text>}
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center', gap: 12, marginTop: 20 }}>
          {!!botao && (
            <Pressable onPress={botao.onPress} style={{ paddingVertical: 14, paddingHorizontal: 22, borderRadius: 16, borderWidth: 2, borderColor: '#fff' }}>
              <Text style={{ color: '#fff', fontWeight: '800', fontSize: 16 }}>{botao.label}</Text>
            </Pressable>
          )}
          {a.tipo === 'pausa' && !!onMais5 && (
            <Pressable onPress={onMais5}
              style={{ paddingVertical: 14, paddingHorizontal: 22, borderRadius: 16, borderWidth: 1, borderColor: a.cor }}>
              <Text style={{ color: a.cor, fontWeight: '700', fontSize: 16 }}>{t('+5 min')}</Text>
            </Pressable>
          )}
          <Pressable onPress={onFechar} style={{ paddingVertical: 14, paddingHorizontal: 22, borderRadius: 16, backgroundColor: a.tipo === 'pausa' ? a.cor : '#fff' }}>
            <Text style={{ color: a.tipo === 'pausa' ? '#fff' : a.cor, fontWeight: '800', fontSize: 16 }}>{a.tipo === 'pausa' ? t('Dispensar') : t('Voltar ao foco')}</Text>
          </Pressable>
        </View>
      </View>
    </Modal>
  );
}
